<template>
  <div class="min-h-screen bg-black text-white">
    <!-- Navigation -->
    <NavBar />

    <!-- Hero Section -->
    <section class="relative pt-32 pb-16 px-6 overflow-hidden">
      <!-- Background Elements -->
      <div class="absolute inset-0">
        <div class="absolute top-0 left-1/4 w-96 h-96 bg-brand/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-dark/10 rounded-full blur-3xl"></div>
      </div>

      <div class="relative max-w-7xl mx-auto">
        <!-- Breadcrumb -->
        <div class="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <router-link to="/" class="hover:text-brand transition-colors">Home</router-link>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
          <span class="text-brand">{{ project.name }}</span>
        </div>

        <!-- Project Header -->
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div class="space-y-6">
            <div class="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 border border-brand/30 rounded-full">
              <span class="text-sm font-semibold text-brand">{{ project.badge }}</span>
            </div>

            <h1 class="text-5xl lg:text-6xl font-bold">
              <span class="text-white">{{ project.name }}</span>
            </h1>

            <p class="text-2xl text-brand font-semibold">{{ project.subtitle }}</p>

            <p class="text-xl text-gray-400 leading-relaxed">
              {{ project.description }}
            </p>

            <div class="flex flex-wrap gap-4">
              <div class="flex items-center gap-2 px-4 py-2 bg-gray-900 border border-brand/20 rounded-full">
                <svg class="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span class="text-gray-300">{{ project.location }}</span>
              </div>
              <div class="flex items-center gap-2 px-4 py-2 bg-gray-900 border border-brand/20 rounded-full">
                <svg class="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span class="text-gray-300">{{ project.company }}</span>
              </div>
            </div>

            <button
              @click="scrollToUnits"
              class="group px-8 py-4 bg-gradient-to-r from-brand to-brand-dark rounded-full text-black font-bold text-lg hover:shadow-2xl hover:shadow-brand/50 transition-all duration-300"
            >
              <span class="flex items-center gap-2">
                View Available Units
                <svg class="w-5 h-5 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </span>
            </button>
          </div>

          <!-- Hero Image -->
          <div class="relative">
            <div class="relative rounded-3xl overflow-hidden border border-brand/30 bg-gradient-to-br from-gray-900 to-black p-1">
              <div class="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  :src="project.heroImage"
                  :alt="project.name"
                  class="w-full h-full object-cover"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              </div>
            </div>
            <!-- Decorative Elements -->
            <div class="absolute -top-4 -right-4 w-24 h-24 bg-brand/20 rounded-full blur-2xl"></div>
            <div class="absolute -bottom-4 -left-4 w-32 h-32 bg-brand-dark/20 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Property Units Section -->
    <section id="units" class="relative py-24 px-6 overflow-hidden bg-gradient-to-b from-gray-950 to-black">
      <!-- Background Elements -->
      <div class="absolute inset-0">
        <div class="absolute top-1/4 right-0 w-96 h-96 bg-brand/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-1/4 left-0 w-96 h-96 bg-brand-dark/10 rounded-full blur-3xl"></div>
      </div>

      <div class="relative max-w-7xl mx-auto">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 border border-brand/30 rounded-full mb-6">
            <svg class="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span class="text-sm font-semibold text-brand">Available Units</span>
          </div>
          <h2 class="text-5xl lg:text-6xl font-bold mb-6">
            <span class="text-white">Choose Your </span>
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-dark">Dream Home</span>
          </h2>
          <p class="text-xl text-gray-400 max-w-3xl mx-auto">
            Explore our exclusive property units within {{ project.name }}, each designed with premium finishes and modern amenities.
          </p>
        </div>

        <!-- Property Units Grid -->
        <PropertyUnits />

        <!-- Contact CTA -->
        <div class="mt-20 relative bg-gradient-to-r from-gray-900 via-black to-gray-900 border border-brand/30 rounded-3xl p-10 overflow-hidden">
          <div class="absolute top-0 right-0 w-64 h-64 bg-brand/10 rounded-full blur-3xl"></div>
          <div class="relative flex flex-col lg:flex-row items-center justify-between gap-8">
            <div class="flex-1 text-center lg:text-left">
              <h3 class="text-3xl font-bold text-white mb-4">Interested in {{ project.name }}?</h3>
              <p class="text-gray-400 text-lg">
                Contact us today to schedule a viewing or get more information about available units and pricing.
              </p>
            </div>
            <div class="flex flex-col sm:flex-row gap-4">
              <router-link
                to="/#contact"
                class="px-8 py-4 bg-gradient-to-r from-brand to-brand-dark rounded-full text-black font-bold text-lg hover:shadow-2xl hover:shadow-brand/50 transition-all duration-300 whitespace-nowrap text-center"
              >
                Contact Us
              </router-link>
              <a
                href="tel:+2340000000000"
                class="px-8 py-4 border-2 border-brand text-brand rounded-full font-bold text-lg hover:bg-brand hover:text-black transition-all duration-300 whitespace-nowrap text-center"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import NavBar from "@/layout/NavBar.vue";
import PropertyUnits from "@/components/PropertyUnits.vue";

// Project data
const projects = {
  'hutu-prestige': {
    name: 'Hutu Prestige',
    subtitle: 'The Recreational Resort',
    badge: 'AibenProperties',
    description: 'An exceptional recreational haven featuring world-class amenities, entertainment facilities, and leisure spaces designed for ultimate relaxation and family bonding. Experience luxury living at its finest.',
    location: 'Premium Location, Abuja',
    company: 'AibenProperties',
    heroImage: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&h=800&fit=crop'
  },
  'hutu-exclusive': {
    name: 'Hutu Exclusive',
    subtitle: 'The Golf Resort',
    badge: 'MshelHomes Limited',
    description: 'A prestigious golf resort featuring championship courses, elegant clubhouses, and refined living spaces for discerning individuals who appreciate excellence.',
    location: 'Golf Paradise, Abuja',
    company: 'MshelHomes Limited',
    heroImage: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200&h=800&fit=crop'
  },
  'hutu-royal': {
    name: 'Hutu Royal',
    subtitle: 'The Polo Resort',
    badge: 'AibenProperties',
    description: 'An aristocratic polo resort combining equestrian excellence with luxurious accommodations, offering an exclusive sporting and lifestyle experience for the elite.',
    location: 'Equestrian Estate, Abuja',
    company: 'AibenProperties',
    heroImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=800&fit=crop'
  },
  'hutu-pent-haven': {
    name: 'Hutu Pent Haven',
    subtitle: 'The Lake Resort',
    badge: 'MshelHomes Limited',
    description: 'A serene lakeside sanctuary offering breathtaking water views, tranquil surroundings, and premium penthouse residences for those seeking peace and natural beauty.',
    location: 'Lakefront Living, Abuja',
    company: 'MshelHomes Limited',
    heroImage: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1200&h=800&fit=crop'
  }
};

export default {
  name: "ProjectDetailView",
  components: {
    NavBar,
    PropertyUnits,
  },
  data() {
    return {
      project: {}
    };
  },
  created() {
    const slug = this.$route.params.slug;
    this.project = projects[slug] || projects['hutu-prestige'];
  },
  watch: {
    '$route.params.slug': {
      handler(newSlug) {
        this.project = projects[newSlug] || projects['hutu-prestige'];
      }
    }
  },
  methods: {
    scrollToUnits() {
      const unitsSection = document.getElementById('units');
      if (unitsSection) {
        unitsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
};
</script>
